import type { ReactNode } from "react";
import { TeamLogoInline } from "./TeamLogoInline.tsx";
import type { TeamRankingZone } from "./teamRankingTableState.ts";

export type TeamRankingColumnKey = "wins" | "losses" | "winPercentage" | "secondary" | "recent";

export type TeamRankingColumn = {
  key: TeamRankingColumnKey;
  kind: "number" | "percentage" | "text";
  label: ReactNode;
};

export type TeamRankingRow = {
  imgURL?: string;
  imgURLSmall?: string;
  isUserTeam?: boolean;
  rank: number | null;
  teamName: ReactNode;
  tid: number;
  values: Partial<Record<TeamRankingColumnKey, ReactNode>>;
};

export type TeamRankingSection = {
  id: string;
  label?: ReactNode;
  rows: readonly TeamRankingRow[];
  zone?: TeamRankingZone;
};

export type TeamRankingTableProps = {
  ariaLabel: string;
  columns: readonly TeamRankingColumn[];
  onTeamSelect?: (tid: number) => void;
  rankLabel: ReactNode;
  seasonNotStartedLabel: ReactNode;
  seasonStarted: boolean;
  sections: readonly TeamRankingSection[];
  teamLabel: ReactNode;
};

const cellClassNames: Record<TeamRankingColumnKey, string> = {
  losses: "team-ranking-cell-losses",
  recent: "team-ranking-cell-recent",
  secondary: "team-ranking-cell-secondary",
  winPercentage: "team-ranking-cell-percentage",
  wins: "team-ranking-cell-wins",
};

const colClassNames: Record<TeamRankingColumnKey, string> = {
  losses: "team-ranking-col-losses",
  recent: "team-ranking-col-recent",
  secondary: "team-ranking-col-secondary",
  winPercentage: "team-ranking-col-percentage",
  wins: "team-ranking-col-wins",
};

export const TeamRankingTable = ({
  ariaLabel,
  columns,
  onTeamSelect,
  rankLabel,
  seasonNotStartedLabel,
  seasonStarted,
  sections,
  teamLabel,
}: TeamRankingTableProps) => {
  const columnCount = columns.length + 2;

  return (
    <div className="team-ranking-table-wrap" data-team-ranking-table="">
      <table aria-label={ariaLabel} className="team-ranking-table table align-middle mb-0">
        <colgroup>
          <col className="team-ranking-col-rank" />
          <col className="team-ranking-col-team" />
          {columns.map((column) => (
            <col className={colClassNames[column.key]} key={column.key} />
          ))}
        </colgroup>
        <thead className="team-ranking-table-header" data-team-ranking-header="">
          <tr>
            <th className="team-ranking-cell-rank" scope="col">
              {rankLabel}
            </th>
            <th className="team-ranking-cell-team" scope="col">
              {teamLabel}
            </th>
            {columns.map((column) => (
              <th
                className={cellClassNames[column.key]}
                data-team-ranking-column={column.key}
                data-team-ranking-column-kind={column.kind}
                key={column.key}
                scope="col"
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!seasonStarted ? (
            <tr className="team-ranking-table-empty" data-team-ranking-empty="">
              <td className="py-4 text-center text-body-secondary" colSpan={columnCount}>
                {seasonNotStartedLabel}
              </td>
            </tr>
          ) : (
            sections.map((section) => (
              <RankingSection
                columns={columns}
                key={section.id}
                onTeamSelect={onTeamSelect}
                section={section}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

const RankingSection = ({
  columns,
  onTeamSelect,
  section,
}: {
  columns: readonly TeamRankingColumn[];
  onTeamSelect?: (tid: number) => void;
  section: TeamRankingSection;
}) => (
  <>
    {section.label !== undefined ? (
      <tr
        className="team-ranking-table-zone"
        data-team-ranking-section={section.id}
        data-team-ranking-zone={section.zone ?? "none"}
      >
        <th colSpan={columns.length + 2} scope="rowgroup">
          {section.label}
        </th>
      </tr>
    ) : null}
    {section.rows.map((row) => (
      <tr
        className={`team-ranking-table-row${row.isUserTeam ? " bg-info-subtle" : ""}`}
        data-team-ranking-row=""
        data-team-ranking-team-id={row.tid}
        data-team-ranking-user-team={row.isUserTeam ? "true" : undefined}
        data-team-ranking-zone={section.zone ?? "none"}
        key={row.tid}
      >
        <td className="team-ranking-cell-rank">{row.rank}</td>
        <th className="team-ranking-cell-team" scope="row">
          <div className="d-flex align-items-center min-width-0">
            <div className="team-ranking-logo flex-shrink-0" data-team-ranking-logo="">
              <TeamLogoInline
                alt=""
                imgURL={row.imgURL}
                imgURLSmall={row.imgURLSmall}
                includePlaceholderIfNoLogo
                size={32}
              />
            </div>
            {onTeamSelect ? (
              <button
                className="btn btn-link p-0 text-start text-truncate"
                data-team-ranking-team-trigger={row.tid}
                onClick={() => onTeamSelect(row.tid)}
                type="button"
              >
                {row.teamName}
              </button>
            ) : (
              <span className="text-truncate">{row.teamName}</span>
            )}
          </div>
        </th>
        {columns.map((column) => (
          <td
            className={cellClassNames[column.key]}
            data-team-ranking-column={column.key}
            data-team-ranking-column-kind={column.kind}
            key={column.key}
          >
            {row.values[column.key] ?? "—"}
          </td>
        ))}
      </tr>
    ))}
  </>
);

export default TeamRankingTable;
